import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons } from '@/ds/icons'
import { DsSkeleton } from '@/ds/skeleton'

export type DsAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type DsAvatarStatus = 'online' | 'away' | 'offline'

export const avatarPx: Record<DsAvatarSize, number> = { xs: 24, sm: 32, md: 40, lg: 56, xl: 80 }

const TONES: DsTone[] = ['brand', 'info', 'success', 'warning', 'neutral']

/** Same hash as avatar.dart (h*31+u) so a person gets the same colour on web and mobile. */
export function avatarTone(name: string): DsTone {
  let h = 0
  for (const ch of name) h = (Math.imul(h, 31) + ch.codePointAt(0)!) | 0
  return TONES[Math.abs(h) % TONES.length]
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export interface DsAvatarProps {
  imageUrl?: string
  name?: string
  size?: DsAvatarSize
  status?: DsAvatarStatus
  onClick?: () => void
  onEdit?: () => void
  editLabel?: string
  loading?: boolean
  ring?: boolean
  semanticLabel?: string
  className?: string
}

export function DsAvatar({ imageUrl, name, size = 'md', status, onClick, onEdit, editLabel = 'Change photo', loading, ring, semanticLabel, className }: DsAvatarProps) {
  const d = avatarPx[size]
  const tone = name ? toneClass[avatarTone(name)] : toneClass.neutral
  const label = semanticLabel ?? name ?? 'Avatar'
  if (loading) return <DsSkeleton height={d} circle className={className} />
  const dot = Math.min(16, Math.max(8, Math.round(d * 0.28)))
  const body = (
    <span
      role="img"
      aria-label={label}
      className={cn('relative inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold', tone.soft, tone.onSoft, ring && 'ring-2 ring-offset-2 ring-offset-background ring-brand-vivid', className)}
      style={{ width: d, height: d, fontSize: Math.round(d * 0.38) }}
    >
      {imageUrl ? <img src={imageUrl} alt="" className="size-full rounded-full object-cover" /> : name ? initialsOf(name) : <DsIconGlyph icon={DsIcons.person} size={Math.round(d * 0.5)} />}
      {status && (
        <span aria-label={status} className={cn('absolute bottom-0 right-0 rounded-full border-2 border-card', status === 'online' && 'bg-success-vivid', status === 'away' && 'bg-warning-vivid', status === 'offline' && 'bg-neutral-vivid')} style={{ width: dot, height: dot }} />
      )}
      {onEdit && (
        <span className="absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center rounded-full border-2 border-card bg-brand text-brand-foreground" style={{ width: dot + 8, height: dot + 8 }}>
          <DsIconGlyph icon={DsIcons.camera} size={dot - 2} />
        </span>
      )}
    </span>
  )
  const handler = onEdit ?? onClick
  if (!handler) return body
  return (
    <button type="button" onClick={handler} aria-label={onEdit ? editLabel : label} className="inline-flex rounded-full outline-none focus-visible:ring-focus">
      {body}
    </button>
  )
}

export interface DsAvatarData {
  name?: string
  imageUrl?: string
}

export function DsAvatarGroup({ avatars, max = 3, size = 'sm', className }: { avatars: DsAvatarData[]; max?: number; size?: DsAvatarSize; className?: string }) {
  const d = avatarPx[size]
  const shown = avatars.slice(0, max)
  const rest = avatars.length - shown.length
  return (
    <span className={cn('inline-flex items-center', className)} role="group" aria-label={`${avatars.length} people`}>
      {shown.map((a, i) => (
        <span key={i} className="rounded-full ring-2 ring-card" style={{ marginLeft: i ? -d * 0.3 : 0 }}>
          <DsAvatar name={a.name} imageUrl={a.imageUrl} size={size} />
        </span>
      ))}
      {rest > 0 && (
        <span className="inline-flex items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground ring-2 ring-card" style={{ width: d, height: d, marginLeft: -d * 0.3, fontSize: Math.round(d * 0.36) }}>
          +{rest}
        </span>
      )}
    </span>
  )
}
